import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  getAdminPurchaseEventDetail,
  retryAdminPurchaseEvent,
  type AdminLinkedUserSummary,
  type AdminPurchaseEventDetailResponse,
  type AdminPurchaseEventRetryResponse,
  type AdminPurchaseEventSummary,
  type AdminReplayEligibility,
} from "@/features/admin/adminApi";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "processed":
      return "bg-emerald-100 text-emerald-800";
    case "failed":
    case "invalid_signature":
      return "bg-red-100 text-red-800";
    case "duplicate":
      return "bg-amber-100 text-amber-800";
    case "unknown_product":
      return "bg-orange-100 text-orange-800";
    case "received":
      return "bg-blue-100 text-blue-800";
    case "refunded":
      return "bg-purple-100 text-purple-800";
    case "ignored":
      return "bg-slate-100 text-slate-600";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(status)}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="mt-1 break-all text-sm font-semibold text-slate-800">
        {value ?? "—"}
      </div>
    </div>
  );
}

function EventSummaryCard({ event }: { event: AdminPurchaseEventSummary }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-indigo-700">Event</p>
        <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
          {event.sourceLabel}
        </span>
        <StatusBadge status={event.status} />
        {event.ghlSent ? (
          <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">
            GHL sent
          </span>
        ) : null}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Event type" value={event.eventType} />
        <DetailRow label="Provider" value={event.provider} />
        <DetailRow label="Tier granted / action" value={event.tierGranted} />
        <DetailRow label="Email" value={event.email} />
        <DetailRow label="User ID" value={event.userId} />
        <DetailRow label="Product ID" value={event.productId} />
        <DetailRow label="Price ID" value={event.priceId} />
        <DetailRow
          label="Transaction ID"
          value={
            event.transactionId ? (
              <span className="font-mono">{event.transactionId}</span>
            ) : null
          }
        />
        <DetailRow
          label="Event ID"
          value={
            event.eventId ? (
              <span className="font-mono">{event.eventId}</span>
            ) : null
          }
        />
        <DetailRow
          label="Internal ID"
          value={<span className="font-mono text-xs">{event.id}</span>}
        />
        <DetailRow label="Received" value={formatDate(event.createdAt)} />
        <DetailRow label="Processed at" value={formatDate(event.processedAt)} />
      </div>

      {event.errorMessage ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-semibold text-red-700">Error</p>
          <p className="mt-1 text-sm text-red-800">{event.errorMessage}</p>
        </div>
      ) : null}
    </section>
  );
}

function LinkedUserCard({ user }: { user: AdminLinkedUserSummary }) {
  const tierLabel =
    user.tier === "pro"
      ? "Pro"
      : user.is_lifetime
        ? "Lifetime Standard"
        : user.tier.charAt(0).toUpperCase() + user.tier.slice(1);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-indigo-700">Linked User</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Name" value={user.name} />
        <DetailRow label="Email" value={user.email} />
        <DetailRow label="Tier" value={tierLabel} />
        <DetailRow label="Pro status" value={user.pro_subscription_status} />
        <DetailRow label="Admin" value={user.is_admin ? "Yes" : "No"} />
        <DetailRow
          label="User ID"
          value={<span className="font-mono text-xs">{user.id}</span>}
        />
      </div>
      <div className="mt-4">
        <Link
          to={`/admin/users/${encodeURIComponent(user.id)}`}
          className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          View full user record →
        </Link>
      </div>
    </section>
  );
}

function RedactedPayloadCard({
  payload,
}: {
  payload: Record<string, unknown>;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-indigo-700">
          Normalised Payload
        </p>
        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
          Sensitive keys redacted
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Raw payload is not shown. This is the normalised representation with
        sensitive fields replaced by [redacted].
      </p>
      <pre className="mt-3 overflow-x-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </section>
  );
}

function RetryPanel({
  eligibility,
  eventId,
  onRetrySuccess,
}: {
  eligibility: AdminReplayEligibility;
  eventId: string;
  onRetrySuccess: () => void;
}) {
  const { getToken } = useAuth();
  const [reason, setReason] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<AdminPurchaseEventRetryResponse | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  const canSubmit =
    eligibility.eligible &&
    reason.trim().length >= 5 &&
    isConfirmed &&
    !isRetrying &&
    !retryResult?.retrySucceeded;

  async function handleRetry() {
    if (!canSubmit) return;
    setIsRetrying(true);
    setRetryError(null);
    setRetryResult(null);

    try {
      const token = await getToken();
      const result = await retryAdminPurchaseEvent(token, eventId, {
        reason: reason.trim(),
        confirm: true,
      });
      setRetryResult(result);
      if (result.retrySucceeded) {
        onRetrySuccess();
      }
    } catch (err) {
      setRetryError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Retry request failed.",
      );
    } finally {
      setIsRetrying(false);
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-indigo-700">
          Retry event processing
        </p>
        {eligibility.eligible ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            Retry available
          </span>
        ) : (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
            Not retryable
          </span>
        )}
      </div>

      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
        <p className="text-xs text-amber-800">
          Retrying event processing may update product access if the original
          event failed to process correctly. This does not charge, refund, or
          cancel payments.
        </p>
      </div>

      <p className="mt-3 text-sm text-slate-600">{eligibility.reason}</p>

      {!eligibility.eligible ? (
        <p className="mt-2 text-sm font-medium text-slate-500">
          This event cannot be retried from the admin panel.
        </p>
      ) : retryResult?.retrySucceeded ? (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-sm font-semibold text-emerald-800">
            Retry succeeded
          </p>
          <p className="mt-1 text-sm text-emerald-700">{retryResult.message}</p>
          {retryResult.auditEventId ? (
            <p className="mt-1 font-mono text-xs text-emerald-600">
              Audit event: {retryResult.auditEventId}
            </p>
          ) : null}
          <p className="mt-2 text-xs text-emerald-600">
            This page has reloaded to reflect the updated event state.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {retryResult && !retryResult.retrySucceeded ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800">Retry failed</p>
              <p className="mt-1 text-sm text-red-700">
                {retryResult.errorMessage ?? retryResult.message}
              </p>
            </div>
          ) : null}
          {retryError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{retryError}</p>
            </div>
          ) : null}
          <div>
            <label
              htmlFor="retry-reason"
              className="block text-xs font-medium text-slate-700"
            >
              Reason for retry (required)
            </label>
            <textarea
              id="retry-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this event needs to be retried…"
              rows={3}
              maxLength={500}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <p className="mt-0.5 text-xs text-slate-400">
              {reason.length}/500 characters (minimum 5)
            </p>
          </div>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="mt-0.5 rounded"
            />
            <span className="text-sm text-slate-700">
              I confirm I want to retry event processing for this event.
            </span>
          </label>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleRetry}
            className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRetrying ? "Retrying…" : "Retry event processing"}
          </button>
        </div>
      )}
    </section>
  );
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        Purchase event details are only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminPurchaseEventDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken, isLoaded } = useAuth();
  const [data, setData] = useState<AdminPurchaseEventDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    async function loadEvent() {
      if (!isLoaded || !id) return;

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await getAdminPurchaseEventDetail(token, id));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          if (err.status === 404) {
            setError("Purchase event not found.");
          } else {
            setIsDenied(true);
          }
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : "Unable to load event details right now.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadEvent();
  }, [id, getToken, isLoaded, refreshKey]);

  if (isDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/purchase-events"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Purchase Events
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Purchase Event Detail
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Read-only view of a single purchase, webhook or manual access event.
          Retry is available for eligible failed events.
        </p>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-48 animate-pulse rounded-lg bg-white" />
          <div className="h-32 animate-pulse rounded-lg bg-white" />
          <div className="h-32 animate-pulse rounded-lg bg-white" />
        </div>
      ) : data ? (
        <>
          <EventSummaryCard event={data.event} />
          {data.linkedUser ? <LinkedUserCard user={data.linkedUser} /> : null}
          {data.redactedPayload ? (
            <RedactedPayloadCard payload={data.redactedPayload} />
          ) : (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-slate-500">Payload</p>
              <p className="mt-1 text-sm text-slate-500">
                No normalised payload recorded for this event.
              </p>
            </section>
          )}
          <RetryPanel
            eligibility={data.eligibility}
            eventId={data.event.id}
            onRetrySuccess={() => setRefreshKey((k) => k + 1)}
          />
        </>
      ) : null}
    </div>
  );
}
